import React, { FunctionComponent, useEffect, useState } from 'react';

import { Error, Field } from '../../contact-form/contact-form-types';
import { ICheckboxProps } from '../checkbox/checkbox';
import { CheckboxTooltip } from '../checkboxTooltip/checkboxTooltip';

import styles from './groupedRadioButtons.module.scss';

export interface IGroupedCheckboxesProps {
  checkboxes: ICheckboxProps[];
  checkIsValid?: boolean;
  errorMessage: string;
  formFields?: Field;
  groupName: string;
  multipleChoiceCheckboxes: boolean;
  onChange?: (event: React.SyntheticEvent, groupName: string) => void;
  required: boolean;
  setError?: (errors: Error) => void;
  subtitle?: string;
  title: string;
  type: string;
}

export const GroupedRadioButtonsComponent: FunctionComponent<IGroupedCheckboxesProps> = ({
  checkboxes,
  checkIsValid,
  errorMessage,
  formFields,
  groupName,
  onChange,
  required,
  setError,
  subtitle,
  title
}) => {
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!checkIsValid || !required) return;

    if (formFields && Boolean(!formFields[groupName])) {
      if (typeof setError !== 'function') return;
      setError({ [groupName]: true });
      setIsValid(false);
    }
  }, [checkIsValid, formFields, groupName, required, setError]);

  const handleChange = (e: React.SyntheticEvent): void => {
    if (typeof onChange !== 'function') return;
    onChange(e, groupName);
  };

  const isChecked = (value: string): boolean =>
    Boolean(formFields && formFields[groupName] === value);

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>{title}</h3>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      <div className={styles.checkboxWrapper}>
        {checkboxes.map(({ label, image, tooltipText, value }, index) => (
          <div key={label} className={styles.labelWrapper}>
            <label
              className={`${styles.label} ${
                isChecked(value as string) ? styles.checked : ''
              }`}
              onClick={(): void => {
                if (typeof setError !== 'function') return;
                setError({ [groupName]: false });
                setIsValid(true);
              }}
            >
              <input
                type="radio"
                onChange={handleChange}
                value={value as string}
                checked={isChecked(value as string)}
              />
              {image && <img src={image} alt="" />}
              <p dangerouslySetInnerHTML={{ __html: label }}></p>
            </label>
            <CheckboxTooltip tooltipText={tooltipText} />
          </div>
        ))}
      </div>
      {!isValid && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
};

export const GroupedRadioButtons = React.memo(GroupedRadioButtonsComponent);
